import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, firstValueFrom, Observable, of } from 'rxjs';
import { ApiUrlBuilder } from '../../config/api.config';
import { AuthService } from '../auth.service';
import {
  FeeConfiguration,
  FeeConfigurationSnapshot,
  SizeRange,
  SizeRangeCreateRequest,
  SizeRangeUpdateRequest,
  SizeRangeDeactivateRequest,
  SectorMultiplier,
  SectorMultiplierUpdateRequest,
  SectorMultiplierCreateRequest,
  DiscountTier,
  DiscountTierCreateRequest,
  DiscountTierUpdateRequest,
  AbsoluteCap,
  AbsoluteCapUpdateRequest,
  FeePreviewResult,
  UserTierInfo,
} from '../../models/fee-configuration.model';

@Injectable({ providedIn: 'root' })
export class FeeConfigurationApiService {
  readonly http = inject(HttpClient);
  readonly authService = inject(AuthService);
  private readonly transloco = inject(TranslocoService);

  async getSnapshot(): Promise<FeeConfigurationSnapshot> {
    return firstValueFrom(
      this.http
        .get<FeeConfigurationSnapshot>(ApiUrlBuilder.feeConfigurations('getAll'), {
          headers: this.authService.getAuthHeaders(),
        })
        .pipe(
          catchError((error) => {
            console.error('Erro ao carregar configurações de taxas:', error);
            return of({ sizeRanges: [], sectorMultipliers: [], tiers: [], absoluteCaps: [] });
          }),
        ),
    );
  }

  async createSizeRange(data: SizeRangeCreateRequest): Promise<SizeRange> {
    return firstValueFrom(
      this.http.post<SizeRange>(ApiUrlBuilder.feeConfigurations('sizeRanges'), data, {
        headers: this.authService.getAuthHeaders(),
      }),
    );
  }

  async updateSizeRange(id: string, data: SizeRangeUpdateRequest): Promise<SizeRange> {
    return firstValueFrom(
      this.http.put<SizeRange>(ApiUrlBuilder.feeConfigurations('sizeRanges', id), data, {
        headers: this.authService.getAuthHeaders(),
      }),
    );
  }

  async deactivateSizeRange(id: string, data: SizeRangeDeactivateRequest): Promise<SizeRange> {
    return firstValueFrom(
      this.http.post<SizeRange>(`${ApiUrlBuilder.feeConfigurations('sizeRanges', id)}/deactivate`, data, {
        headers: this.authService.getAuthHeaders(),
      }),
    );
  }

  async reactivateSizeRange(id: string): Promise<SizeRange> {
    return firstValueFrom(
      this.http.post<SizeRange>(
        `${ApiUrlBuilder.feeConfigurations('sizeRanges', id)}/reactivate`,
        {},
        {
          headers: this.authService.getAuthHeaders(),
        },
      ),
    );
  }

  async updateSectorMultiplier(id: string, data: SectorMultiplierUpdateRequest): Promise<SectorMultiplier> {
    return firstValueFrom(
      this.http.put<SectorMultiplier>(ApiUrlBuilder.feeConfigurations('sectorMultipliers', id), data, {
        headers: this.authService.getAuthHeaders(),
      }),
    );
  }

  async createSectorMultiplier(data: SectorMultiplierCreateRequest): Promise<SectorMultiplier> {
    return firstValueFrom(
      this.http.post<SectorMultiplier>(ApiUrlBuilder.feeConfigurations('sectorMultipliers'), data, {
        headers: this.authService.getAuthHeaders(),
      }),
    );
  }

  async createTier(data: DiscountTierCreateRequest): Promise<DiscountTier> {
    return firstValueFrom(
      this.http.post<DiscountTier>(ApiUrlBuilder.feeConfigurations('tiers'), data, {
        headers: this.authService.getAuthHeaders(),
      }),
    );
  }

  async updateTier(id: string, data: DiscountTierUpdateRequest): Promise<DiscountTier> {
    return firstValueFrom(
      this.http.put<DiscountTier>(ApiUrlBuilder.feeConfigurations('tiers', id), data, {
        headers: this.authService.getAuthHeaders(),
      }),
    );
  }

  async updateAbsoluteCap(id: string, data: AbsoluteCapUpdateRequest): Promise<AbsoluteCap> {
    return firstValueFrom(
      this.http.put<AbsoluteCap>(ApiUrlBuilder.feeConfigurations('absoluteCaps', id), data, {
        headers: this.authService.getAuthHeaders(),
      }),
    );
  }

  async preview(
    projectTypologyId: string | undefined,
    tierId: string | undefined,
    value: number,
  ): Promise<FeePreviewResult> {
    return firstValueFrom(
      this.http
        .post<FeePreviewResult>(
          ApiUrlBuilder.feeConfigurations('preview'),
          { projectTypologyId, tierId, value },
          { headers: this.authService.getAuthHeaders() },
        )
        .pipe(
          catchError((err) => {
            throw err;
          }),
        ),
    );
  }

  async getMyTier(): Promise<UserTierInfo> {
    return firstValueFrom(
      this.http.get<UserTierInfo>(ApiUrlBuilder.feeConfigurations('myTier'), {
        headers: this.authService.getAuthHeaders(),
      }),
    );
  }

  async getActiveFeeConfiguration(code: string): Promise<FeeConfiguration> {
    return firstValueFrom(
      this.http.get<FeeConfiguration>(ApiUrlBuilder.feeConfigurations('active', code), {
        headers: this.authService.getAuthHeaders(),
      }),
    );
  }

  simulate(projectId: string, value: number): Observable<FeePreviewResult> {
    return this.http.post<FeePreviewResult>(
      ApiUrlBuilder.feeConfigurations('simulate'),
      { projectId, value },
      { headers: this.authService.getAuthHeaders() },
    );
  }

  getApiErrorMessage(error: unknown, fallback: string): string {
    if (!(error instanceof HttpErrorResponse)) return fallback;

    const payload = error.error as unknown;

    if (typeof payload === 'string') return payload;

    if (payload && typeof payload === 'object') {
      const body = payload as { code?: string; message?: string };
      const key = this.getApiErrorTranslationKey(body.code);
      if (key) return this.transloco.translate(key);
      if (body.message?.trim()) return body.message;
    }

    switch (error.status) {
      case 400:
        return this.transloco.translate('shared.apiErrors.invalidDataDetails');
      case 403:
        return this.transloco.translate('shared.apiErrors.forbiddenAction');
      case 500:
        return this.transloco.translate('shared.apiErrors.internalServerRetry');
      default:
        return this.transloco.translate('shared.apiErrors.connectionServer');
    }
  }

  private getApiErrorTranslationKey(code?: string): string | null {
    const codeToKey: Record<string, string> = {
      'Fee.SizeRangesMustCoverAllValues': 'admin.fees.errors.coverageGap',
      'Fee.MultiplierMustBePositive': 'admin.fees.errors.multiplierMustBePositive',
      'Fee.DiscountOutOfRange': 'admin.fees.errors.discountOutOfRange',
      'Fee.CapMustBePositive': 'admin.fees.errors.capMustBePositive',
      'Fee.SizeRangeMustSplitExisting': 'admin.fees.sizeRanges.reactivateConflict',
      'Fee.SizeRangeMustShareBoundary': 'admin.fees.sizeRanges.reactivateConflict',
    };
    return code ? (codeToKey[code] ?? null) : null;
  }
}
